/**
 * Shared brand mark for admin/auth pages.
 * Pulls `logo_url` from the public settings endpoint (same source the
 * Website Builder CMS writes to). Falls back to a gold "A" serif circle
 * when no logo is configured, so the site never renders blank branding.
 */
import React from "react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

export function useBrandSettings() {
  return useQuery({
    queryKey: ["public-settings"],
    queryFn: async () => (await axios.get(`${BACKEND}/api/settings/public`)).data,
    staleTime: 60_000,
  });
}

export default function AdminLogo({
  size = 44,
  variant = "light",     // "light" (dark bg fallback) | "dark" (light bg fallback)
  className = "",
}) {
  const { data } = useBrandSettings();
  const logoUrl = data?.logo_url
    ? (data.logo_url.startsWith("http") ? data.logo_url : `${BACKEND}${data.logo_url}`)
    : null;

  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt={data?.company_name || "Adelina"}
        className={`object-contain rounded-full ${className}`}
        style={{ width: size, height: size }}
        data-testid="admin-brand-logo"
      />
    );
  }
  const bg = variant === "light" ? "bg-white" : "bg-zinc-950";
  const border = variant === "light" ? "border-zinc-200" : "border-[color:var(--brand-primary,#b8860b)]";
  const letterColor = variant === "light" ? "text-zinc-950" : "text-[color:var(--brand-primary,#b8860b)]";
  return (
    <div
      className={`rounded-full flex items-center justify-center border ${bg} ${border} ${className}`}
      style={{ width: size, height: size }}
      data-testid="admin-brand-fallback"
    >
      <span className={`font-serif ${letterColor}`} style={{ fontSize: Math.round(size * 0.5) }}>A</span>
    </div>
  );
}
